/**
 * Four-state rating pills: the control itself shows whether a value is only an
 * AI suggestion (dashed), confirmed (solid + check) or changed by the mentor.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { IcfFormLevel, IcfScaleOption, StatementRatingValue } from '@/data/icfFormStatements';
import type { SofOverrideReason } from '@/types/mentorEngagements';
import { mentorStyles } from '../shared';
import type { ReviewState } from '../sofReview';

const OVERRIDE_REASONS: SofOverrideReason[] = ['wrong_statement', 'misquoted', 'not_the_coach', 'too_weak', 'other'];

interface Props {
  id: string;
  level: IcfFormLevel;
  language: 'en' | 'tr';
  options: IcfScaleOption[];
  value: StatementRatingValue | null;
  aiValue: StatementRatingValue | null;
  state: ReviewState;
  confidence?: number | null;
  disabled: boolean;
  locked: boolean;
  showPcc64Note: boolean;
  overrideReason?: SofOverrideReason | null;
  onChange: (value: StatementRatingValue) => void;
  onUseAi: () => void;
  onOverrideReason: (reason: SofOverrideReason | null) => void;
}

export function SofRatingControl({
  id, level, language, options, value, aiValue, state, confidence, disabled, locked, showPcc64Note, overrideReason, onChange, onUseAi, onOverrideReason,
}: Props) {
  const { t } = useTranslation();
  const labelOf = (option: IcfScaleOption) => (language === 'tr' ? option.label_tr : option.label_en);
  const aiLabel = options.find(option => option.value === aiValue);
  const lowConfidence = confidence != null && confidence < 0.6;

  const caption = (() => {
    switch (state) {
      case 'unrated': return t('mentorEngagements.review.rating.helper');
      case 'ai_suggested': return confidence != null
        ? t('mentorEngagements.review.rating.aiSuggestedPct', { percent: Math.round(confidence * 100) })
        : t('mentorEngagements.review.aiSuggested');
      case 'confirmed': return t('mentorEngagements.review.confirmed');
      case 'overridden': return aiLabel
        ? `${t('mentorEngagements.review.overridden')} · ${t('mentorEngagements.review.state.aiSaid', { label: labelOf(aiLabel) })}`
        : t('mentorEngagements.review.overridden');
    }
  })();

  return (
    <View style={styles.wrap}>
      <View style={styles.pills} testID={`sof_review_rating_${id}`}>
        {options.map(option => {
          const selected = option.value === value;
          const suggested = selected && state === 'ai_suggested';
          const solid = selected && (state === 'confirmed' || state === 'overridden');
          const label = labelOf(option);
          return (
            <Pressable
              key={option.value}
              onPress={() => !disabled && onChange(option.value)}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={label}
              accessibilityHint={option.value === 'not_applicable' ? t('mentorEngagements.review.rating.naDefinition') : undefined}
              accessibilityState={{ selected, disabled }}
              aria-selected={selected}
              style={({ pressed }) => [
                styles.pill,
                suggested && styles.pillSuggested,
                solid && styles.pillSolid,
                disabled && { opacity: 0.55 },
                pressed && mentorStyles.pressed,
              ]}
            >
              {suggested ? <MaterialIcons name="auto-awesome" size={13} color={reviewDesk.accent} /> : null}
              {solid ? <MaterialIcons name="check" size={14} color="#FFFFFF" /> : null}
              <Text style={[styles.pillText, suggested && { color: reviewDesk.accent, fontWeight: '800' }, solid && { color: '#FFFFFF', fontWeight: '800' }]}>{label}</Text>
            </Pressable>
          );
        })}
      </View>
      <View style={styles.captionRow}>
        <Text testID={`sof_review_confirmation_${id}`} style={[styles.caption, state === 'overridden' && { color: '#8A5B00' }]}>{caption}</Text>
        {state === 'overridden' && aiValue !== null && !locked ? (
          <Pressable accessibilityRole="button" disabled={disabled} onPress={onUseAi} style={({ pressed }) => [pressed && mentorStyles.pressed]}>
            <Text style={styles.link}>{t('mentorEngagements.review.useAi')}</Text>
          </Pressable>
        ) : null}
      </View>
      {lowConfidence && state === 'ai_suggested' ? (
        <View style={styles.lowRow}>
          <MaterialIcons name="priority-high" size={14} color="#8A5B00" />
          <Text style={styles.lowText}>{t('mentorEngagements.review.rating.lowConfidence')}</Text>
        </View>
      ) : null}
      {showPcc64Note && level === 'pcc' ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.rating.pcc64')}</Text> : null}
      {state === 'overridden' && aiValue !== null && !locked ? (
        <View style={{ gap: 6 }}>
          <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.rating.overrideReason')}</Text>
          <View style={styles.reasons}>
            {OVERRIDE_REASONS.map(reason => {
              const active = overrideReason === reason;
              return (
                <Pressable key={reason} accessibilityRole="button" accessibilityState={{ selected: active, disabled }} disabled={disabled}
                  onPress={() => onOverrideReason(active ? null : reason)}
                  style={({ pressed }) => [styles.reason, active && styles.reasonActive, pressed && mentorStyles.pressed]}>
                  <Text style={[styles.reasonText, active && { color: reviewDesk.accent }]}>{t(`mentorEngagements.review.relevance.reason.${reason}`)}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 8 },
  pills: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    minHeight: 38, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 999,
    borderWidth: 1.5, borderColor: reviewDesk.line, backgroundColor: reviewDesk.card,
  },
  pillSuggested: { borderStyle: 'dashed', borderColor: reviewDesk.accent, backgroundColor: reviewDesk.accentWash },
  pillSolid: { borderColor: reviewDesk.accent, backgroundColor: reviewDesk.accent },
  pillText: { fontSize: 13, fontWeight: '600', color: reviewDesk.inkSoft },
  captionRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  caption: { color: reviewDesk.muted, fontSize: 13, lineHeight: 19, flexShrink: 1 },
  link: { color: reviewDesk.accent, fontSize: 13, fontWeight: '700' },
  lowRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  lowText: { color: '#8A5B00', fontSize: 13, fontWeight: '600' },
  reasons: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  reason: { paddingVertical: 5, paddingHorizontal: 10, borderRadius: 999, borderWidth: 1, borderColor: reviewDesk.line, backgroundColor: reviewDesk.card },
  reasonActive: { borderColor: reviewDesk.accent, backgroundColor: reviewDesk.accentSoft },
  reasonText: { fontSize: 12, fontWeight: '600', color: reviewDesk.inkSoft },
});
